import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
  FullScreenOverlayBody,
  FullScreenOverlayContent,
  FullScreenOverlayHeader
} from '@bibliocommons/base-full-screen-overlay';
import Tabs from '@bibliocommons/base-tabs';
import { libraryShape, subscriptionShape } from '@bibliocommons/bc-prop-types';
import { TableStencil } from '@bibliocommons/table';
import { bindAll } from '@bibliocommons/utils-react';

import ILSOfflineMessage from 'app/components/bibs/shared/ILSOfflineMessage';
import { StackMapDisplayContainer } from 'app/components/bibs/shared/StackMap/StackMapDisplay';
import UniversalBibBriefContainer from 'app/components/bibs/shared/UniversalBibBrief/UniversalBibBriefContainer';
import ItemAvailabilityTable from './ItemAvailabilityTable';
import Subscriptions from './Subscriptions';

import './AvailabilityDetails.scss';

const AVAILABILITY_TABS = {
  CIRCULATION: 'circulation',
  SUBSCRIPTIONS: 'subscription'
};

export class AvailabilityDetails extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  state = {
    stackMapOpen: false,
    selectedTab: AVAILABILITY_TABS.SUBSCRIPTIONS
  };

  handleStackMapOpen = () => {
    this.setState({ stackMapOpen: true });
  };

  handleStackMapClose = () => {
    this.setState({ stackMapOpen: false });
  };

  handleTabSelect = selectedTab => {
    this.setState({ selectedTab });
  };

  trackRequestFormClick({ itemId, linkText }) {
    const { metadataId, trackRequestExternalPlace } = this.props;

    trackRequestExternalPlace({
      bibId: metadataId,
      metadata: {
        metadata_category_type: 'holdings_resource_item_id',
        metadata_value: itemId
      },
      ui: {
        ui_component_label: linkText,
        ui_component_type: 'link-text'
      }
    });
  }

  renderTabContent = () => {
    switch (this.state.selectedTab) {
      case AVAILABILITY_TABS.CIRCULATION:
        return this.renderCirculation();
      case AVAILABILITY_TABS.SUBSCRIPTIONS:
        return this.renderSubscriptions();
      default:
        return null;
    }
  };

  renderCirculation() {
    const { bibItems, currentLibrary, probes } = this.props;

    return (
      <ItemAvailabilityTable
        bibItems={bibItems}
        currentLibrary={currentLibrary}
        handleStackMapOpen={this.handleStackMapOpen}
        probes={probes}
        trackRequestFormClick={this.trackRequestFormClick}
      />
    );
  }

  renderSubscriptions() {
    const { subscriptions } = this.props;
    return <Subscriptions subscriptions={subscriptions} />;
  }

  renderTabs() {
    const tabNames = [AVAILABILITY_TABS.SUBSCRIPTIONS, AVAILABILITY_TABS.CIRCULATION];
    const options = tabNames.map(name => ({
      label: this.props.intl.formatMessage({ id: `availability_modal_tab_${name}` }),
      selected: name === this.state.selectedTab,
      value: name
    }));

    return <Tabs options={options} renderContent={this.renderTabContent} handleSelect={this.handleTabSelect} />;
  }

  renderMainContent() {
    if (this.props.isFetching) return <TableStencil />;
    if (this.state.stackMapOpen) return <StackMapDisplayContainer />;
    if (this.props.bibItems.size > 0 && this.props.subscriptions.size > 0) return this.renderTabs();
    if (this.props.bibItems.size > 0) return this.renderCirculation();
    if (this.props.subscriptions.size > 0) return this.renderSubscriptions();
    return null;
  }

  render() {
    const { availabilityError, isFetching, metadataId } = this.props;

    const title = this.state.stackMapOpen ? (
      <FormattedMessage id="find_item" />
    ) : (
      <FormattedMessage id="availability_details" />
    );

    return (
      <FullScreenOverlayBody className="cp-availability-details">
        <FullScreenOverlayHeader title={title} />
        <FullScreenOverlayContent>
          <UniversalBibBriefContainer metadataId={metadataId} />
          {!isFetching && <ILSOfflineMessage availabilityError={availabilityError} />}
          {this.renderMainContent()}
        </FullScreenOverlayContent>
      </FullScreenOverlayBody>
    );
  }
}

AvailabilityDetails.propTypes = {
  availabilityError: PropTypes.string,
  bibItems: ImmutablePropTypes.list.isRequired,
  currentLibrary: libraryShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  metadataId: PropTypes.string.isRequired,
  probes: ImmutablePropTypes.list.isRequired,
  subscriptions: ImmutablePropTypes.listOf(subscriptionShape).isRequired,
  trackRequestExternalPlace: PropTypes.func.isRequired
};

export default injectIntl(AvailabilityDetails);
